import { t } from '@/i18n';
import { z } from 'zod';

export const amountDifferenceUnit = z.enum(['amount', 'percent']);
export const amountDifferenceOperator = z.enum(['gte', 'lte', 'eq', 'none']);

export const AmountDifferenceOperatorThresholdLabelModifier = {
  gte: t('threshold-greater'),
  lte: t('threshold-lesser'),
  eq: t('threshold-equal'),
  none: t('threshold-none'),
} as const satisfies Record<z.infer<typeof amountDifferenceOperator>, string>;

export const AmountDifferenceOperatorTrendLabelModifier = {
  gte: t('trend-increase'),
  lte: t('trend-decrease'),
  eq: t('trend-no-change'),
} as const satisfies Omit<
  Record<z.infer<typeof amountDifferenceOperator>, string>,
  'none'
>;

export const TableNestSettingModel = z.object({
  project: z.boolean(),
  policyLevel1: z.boolean().optional(), // TODO `optional` is KS and SS only
  policyLevel2: z.boolean().optional(), // TODO `optional` is KS and SS only
  policyLevel3: z.boolean().optional(), // TODO this field is RS only
});

const _TableNestLevelLabelModifier = {
  project: t('project'),
  policyLevel1: t('policy-level-1'),
  policyLevel2: t('policy-level-2'),
  policyLevel3: t('policy-level-3'),
} as const satisfies Record<
  keyof z.infer<typeof TableNestSettingModel>,
  string
>;

export type ProjectTableColumn =
  | 'name'
  | 'fiscalYear'
  | 'responsibleOrganizationName'
  | 'accountingName'
  | 'expenditurePurposeLabelList'
  | 'budgetType'
  | 'budgetData'
  | 'overview'
  | 'sortingCode'
  | 'priority'
  | 'status'
  | 'frameAllocationCategory'
  | 'frameAllocationCurrentYearCurrentBudgetData'
  | 'frameAllocationTotalRevenueNationalTreasuryDisbursements'
  | 'frameAllocationTotalRevenueLocalGovernmentBondsRequested'
  | 'frameAllocationTotalRevenueOthers'
  | 'frameAllocationTotalRevenueGeneral'
  | 'frameAllocationBreakdownLabelAmount'
  | 'frameAllocationCategoryRemainingAmount'
  | 'currentYearLatest'
  | 'totalSpecificRevenueLatest'
  | 'totalRevenueNationalTreasuryDisbursementsLatest'
  | 'totalRevenueLocalGovernmentBondsLatest'
  | 'totalRevenueOthersLatest'
  | 'totalRevenueGeneralLatest'
  | 'totalExpenditureLatest'
  | 'totalSpecificExpenditureLatest'
  | 'totalExpenditureNationalTreasuryDisbursementsLatest'
  | 'totalExpenditureLocalGovernmentBondsLatest'
  | 'totalExpenditureOthersLatest'
  | 'totalExpenditureGeneralLatest'
  | 'currentYearCurrentBudgetData'
  | 'totalSpecificRevenue'
  | 'totalRevenueNationalTreasuryDisbursements'
  | 'totalRevenueLocalGovernmentBondsRequested'
  | 'totalRevenueOthers'
  | 'totalRevenueGeneral'
  | 'totalExpenditureRequested'
  | 'totalSpecificExpenditure'
  | 'totalExpenditureNationalTreasuryDisbursements'
  | 'totalExpenditureLocalGovernmentBondsRequested'
  | 'totalExpenditureOthers'
  | 'totalExpenditureGeneral'
  | 'currentYearAssessed'
  | 'totalSpecificRevenueAssessed'
  | 'totalRevenueNationalTreasuryDisbursementsAssessed'
  | 'totalRevenueLocalGovernmentBondsAssessed'
  | 'totalRevenueOthersAssessed'
  | 'totalRevenueGeneralAssessed'
  | 'totalExpenditureAssessed'
  | 'totalSpecificExpenditureAssessed'
  | 'totalExpenditureNationalTreasuryDisbursementsAssessed'
  | 'totalExpenditureLocalGovernmentBondsAssessed'
  | 'totalExpenditureOthersAssessed'
  | 'totalExpenditureGeneralAssessed'
  | 'currentYearNotified'
  | 'totalSpecificRevenueNotified'
  | 'totalRevenueNationalTreasuryDisbursementsNotified'
  | 'totalRevenueLocalGovernmentBondsNotified'
  | 'totalRevenueOthersNotified'
  | 'totalRevenueGeneralNotified'
  | 'selectedYearDecidedBudgetNotified'
  | 'previousFiscalYearInitialBudget'
  | 'totalExpenditureNotified'
  | 'totalSpecificExpenditureNotified'
  | 'totalExpenditureNationalTreasuryDisbursementsNotified'
  | 'totalExpenditureLocalGovernmentBondsNotified'
  | 'totalExpenditureOthersNotified'
  | 'totalExpenditureGeneralNotified'
  | 'totalDiscontinuedAmount'
  | 'totalDiscontinuedAmountOfSpecialRevenue'
  | 'discontinuedAmountOfNationalTreasuryDisbursement'
  | 'discontinuedAmountOfLocalGovernmentBond'
  | 'discontinuedAmountOfOthers'
  | 'discontinuedAmountOfGeneral';

export const PoliciesTableColumnLabelModifier = {
  policyLevel1Name: t('policy-level-1'),
  policyLevel1Level: t('level'),
  policyLevel2Name: t('policy-level-2'),
  policyLevel2Level: t('level'),
  policyLevel3Name: t('policy-level-3'),
  policyLevel3Level: t('level'),
};

export type ProjectTableColumnCategory =
  | 'basicInformation'
  | 'revenueLatest'
  | 'expenditureLatest'
  | 'revenueRequested'
  | 'expenditureRequested'
  | 'revenueAssessed'
  | 'expenditureAssessed'
  | 'revenueNotified'
  | 'expenditureNotified'
  | 'discontinuedAmount';

export const ProjectTableColumnCategoryLabelModifier = {
  basicInformation: t('basic-information'),
  revenueLatest: t('column-category-revenue-latest'),
  expenditureLatest: t('column-category-expenditure-latest'),
  revenueRequested: t('column-category-revenue-requested'),
  expenditureRequested: t('column-category-expenditure-requested'),
  revenueAssessed: t('column-category-revenue-assessed'),
  expenditureAssessed: t('column-category-expenditure-assessed'),
  revenueNotified: t('column-category-revenue-notified'),
  expenditureNotified: t('column-category-expenditure-notified'),
  discontinuedAmount: t('column-category-discontinued-amount'),
} as const satisfies Record<ProjectTableColumnCategory, string>;

export const trendThresholdBudgetTypeAndAmountOptions = z.enum([
  'allRequestedAmount',
  'initialFinalizedAmount',
  'supplementaryFinalizedAmount',
  'carryOverFinalizedAmount',
  'reserveFinalizedAmount',
  'allFinalizedAmount',
  'allExecutedAmount',
  'executionRate',
]);

export const administrativeReformTeamEvaluationOptions = z.enum([
  'false',
  'true',
]);

export const AdministrativeReformTeamEvaluationOptionLabelModifier = {
  false: t('filter-by-non-evaluation-skip'),
  true: t('filter-by-evaluation-skip'),
} as const satisfies Record<
  z.infer<typeof administrativeReformTeamEvaluationOptions>,
  string
>;

export const TrendThresholdBudgetTypeAndAmountLabelModifier = {
  allRequestedAmount: t('all-requested-amount'),
  initialFinalizedAmount: t('initial-finalized-amount'),
  supplementaryFinalizedAmount: t('supplementary-finalized-amount'),
  carryOverFinalizedAmount: t('carry-over-finalized-amount'),
  reserveFinalizedAmount: t('reserve-finalized-amount'),
  allFinalizedAmount: t('all-finalized-amount'),
  allExecutedAmount: t('all-executed-amount'),
  executionRate: t('execution-rate'),
} as const satisfies Record<
  z.infer<typeof trendThresholdBudgetTypeAndAmountOptions>,
  string
>;

export const budgetAmountTypeEnum = z.enum([
  'requested',
  'finalized',
  'executed',
  'execution_rate',
]);

export const BudgetTypeEnum = z.enum([
  'initial',
  'supplementary',
  'carried-forward',
  'reserve',
  'all',
]);

export const TrendThresholdValueMap = {
  allRequestedAmount: { budgetType: 'all', budgetAmount: 'requested' },
  initialFinalizedAmount: {
    budgetType: 'initial',
    budgetAmount: 'finalized',
  },
  supplementaryFinalizedAmount: {
    budgetType: 'supplementary',
    budgetAmount: 'finalized',
  },
  carryOverFinalizedAmount: {
    budgetType: 'carried-forward',
    budgetAmount: 'finalized',
  },
  reserveFinalizedAmount: {
    budgetType: 'reserve',
    budgetAmount: 'finalized',
  },
  allFinalizedAmount: { budgetType: 'all', budgetAmount: 'finalized' },
  allExecutedAmount: { budgetType: 'all', budgetAmount: 'executed' },
  executionRate: { budgetType: 'all', budgetAmount: 'execution_rate' },
} as const satisfies Record<
  z.infer<typeof trendThresholdBudgetTypeAndAmountOptions>,
  {
    budgetType: z.infer<typeof BudgetTypeEnum>;
    budgetAmount: z.infer<typeof budgetAmountTypeEnum>;
  }
>;
